// Davetiye (rezervasyon_onay) WhatsApp gönderim durumu. Toplu gönderici her
// paid rezervasyona mesajı 5 sn arayla yollarken bu sayfa 4 sn'de bir yenilenip
// "kime gitti / kime gitmedi" tablosunu canlı gösterir.

import { CheckCircle2, Clock, RefreshCw, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi } from './auth.ts';
import { Badge, Button, Card, PageHeader, StatTile, useToast } from './ui/index.ts';

type Item = {
  id: string;
  referenceCode: string;
  fullName: string;
  guardianName: string | null;
  phone: string;
  seatCount: number;
  whatsappStatus: 'sent' | 'failed' | null;
  whatsappSentAt: string | null;
  whatsappError: string | null;
};
type Summary = { total: number; sent: number; failed: number; pending: number };
type Resp = { summary: Summary; items: Item[] };

const POLL_MS = 4000;

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('tr-TR', {
      timeZone: 'Europe/Istanbul',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date(iso));
  } catch {
    return '—';
  }
}

export function WhatsappStatus() {
  const toast = useToast();
  const [data, setData] = useState<Resp | null>(null);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(true);
  const liveRef = useRef(live);
  liveRef.current = live;

  const load = useCallback(
    async (showSpinner = false) => {
      if (showSpinner) setLoading(true);
      try {
        const resp = await adminApi<Resp>('/admin/whatsapp-status');
        setData(resp);
      } catch {
        if (showSpinner) toast.error('Durum yüklenemedi.');
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    load(true);
  }, [load]);

  // Poll while "canlı" is on. Auto-stops polling once nothing is pending, but
  // keeps a slow heartbeat so late retries still surface.
  useEffect(() => {
    const t = setInterval(() => {
      if (liveRef.current) load(false);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const s = data?.summary;
  const items = data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Davetiye"
        title="WhatsApp Gönderim"
        description="Onaylanan rezervasyonlara davetiye mesajının gidip gitmediği — canlı durum."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setLive((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                live
                  ? 'border-success/40 bg-[rgba(106,168,79,0.12)] text-success'
                  : 'border-border text-text-muted hover:border-border-strong'
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${live ? 'animate-pulse bg-success' : 'bg-text-faint'}`}
                aria-hidden="true"
              />
              {live ? 'Canlı' : 'Duraklatıldı'}
            </button>
            <Button variant="secondary" onClick={() => load(true)}>
              <RefreshCw className="h-4 w-4" />
              Yenile
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Toplam" value={s?.total ?? '—'} />
        <StatTile
          label="Gönderildi"
          value={s?.sent ?? '—'}
          accent="success"
          icon={<CheckCircle2 className="h-4 w-4" />}
        />
        <StatTile
          label="Bekliyor"
          value={s?.pending ?? '—'}
          accent="gold"
          icon={<Clock className="h-4 w-4" />}
        />
        <StatTile
          label="Hata"
          value={s?.failed ?? '—'}
          accent="crimson"
          icon={<XCircle className="h-4 w-4" />}
        />
      </div>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-text-faint">
                <th className="px-4 py-2.5 font-medium">Ref</th>
                <th className="px-4 py-2.5 font-medium">Öğrenci</th>
                <th className="px-4 py-2.5 font-medium">Veli</th>
                <th className="px-4 py-2.5 font-medium">Telefon</th>
                <th className="px-4 py-2.5 text-center font-medium">Koltuk</th>
                <th className="px-4 py-2.5 font-medium">Durum</th>
                <th className="px-4 py-2.5 font-medium">Saat / Sebep</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-text-faint">
                    Yükleniyor…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-text-faint">
                    Onaylanmış rezervasyon yok.
                  </td>
                </tr>
              )}
              {items.map((it) => (
                <tr key={it.id} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-2.5 font-mono text-xs text-text-muted">
                    {it.referenceCode}
                  </td>
                  <td className="px-4 py-2.5 text-text">{it.fullName}</td>
                  <td className="px-4 py-2.5 text-text-muted">{it.guardianName ?? '—'}</td>
                  <td className="px-4 py-2.5 tnum text-text-muted">{it.phone}</td>
                  <td className="px-4 py-2.5 text-center tnum text-text-muted">{it.seatCount}</td>
                  <td className="px-4 py-2.5">
                    {it.whatsappStatus === 'sent' ? (
                      <Badge tone="success" dot>
                        Gönderildi
                      </Badge>
                    ) : it.whatsappStatus === 'failed' ? (
                      <Badge tone="danger" dot>
                        Hata
                      </Badge>
                    ) : (
                      <Badge tone="gold" dot>
                        Bekliyor
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-text-faint">
                    {it.whatsappStatus === 'failed' ? (
                      <span className="text-accent">{it.whatsappError ?? 'bilinmiyor'}</span>
                    ) : (
                      fmtTime(it.whatsappSentAt)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
